import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import MeetingSessionMediaPlayer, { MeetingSessionMediaPlayerProps } from "../../../../components/player/MeetingSessionMediaPlayer";
import { useFocusedMeetingAPI, useMeetingSelector } from "../../../../hooks/meeting";
import { MeetingSessionOvenMediaReceiverManagerBaseData, useOvenMediaVideoSubscriptions } from "@kalyzee/kast-web-sdk";
import { useEffect, useMemo, useState } from "react";
import { useStudentContext } from "../context";

import { HoverableButton, PedagoPopup, PlayerContainer, ReactIcons, Text } from "@kalyzee/escaet-ui";

import { MeetingSessionHighlightingState } from "@kalyzee/kast-web-sdk/websocket";
import { MeetingPlayerTriLayoutContainer } from "../../../../components/player/container/MeetingPlayerTriLayoutContainer";
import { PlayerItem } from "../../../../components/player/interfaces";
import "./dashboard.css";
import styles from "./dashboard.module.css";
import { expertWebcamName } from "../../../../helpers/webcam";

export const MeetingDashboardStudentPage = () => {
  const studentCtx = useStudentContext();

  const meetingCtx = useFocusedMeetingAPI();
  const [meeting] = useMeetingSelector(meetingCtx, (state) => state.meeting);

  const [focusedVideoSessionId, setFocusedVideoSessionId] = useState<string>();

  const subscribptionsAPI = useOvenMediaVideoSubscriptions();

  const expertNonWebcamVideoSessions = useMemo(
    () => (studentCtx?.currentView?.experts ?? [])
      .flatMap(data => data.session.videoSessions)
      .filter(videoSession => videoSession.data.mediaSession.name !== expertWebcamName), 
    [studentCtx],
  );

  // Enabled : enabled videos in the left drawer
  const { enabledVideoSessions } = useMemo(() => {
    if (!studentCtx || !meeting) return { enabledVideoSessions: [] };
    const studentMeetingOptions = studentCtx.getMeetingOptions(meeting.id);

    let enabledVideoSessions = expertNonWebcamVideoSessions;

    const enabledVideoSessionsIds = studentMeetingOptions?.enabledVideoSessionIds;
    if (enabledVideoSessionsIds?.length) {
      enabledVideoSessions = enabledVideoSessions.filter((s) => enabledVideoSessionsIds.includes(s.data.mediaSession.id));
    }

    return { enabledVideoSessions };
  }, [expertNonWebcamVideoSessions, studentCtx, meetingCtx]);

  const playerItems = useMemo(() => enabledVideoSessions.map(
    (session) =>
      new PlayerItem({
        type: "video_session",
        videoSession: session,
      })
  ), [enabledVideoSessions]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    subscribptionsAPI.update(enabledVideoSessions.map((s) => ({ session: s, active: true })));
    return () => subscribptionsAPI.update(enabledVideoSessions.map((s) => ({ session: s, active: false })));
  }, [subscribptionsAPI, enabledVideoSessions]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!studentCtx) return null;
  if (!meeting) return null;

  const { layout: layoutIndex } = studentCtx;

  const renderPlayerHeader = (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>, focusMode: boolean) => {
    const meetingSession = session.data.session;
    const isPedagogy = meetingSession.extra?.isPedagogy;
    const username = meetingSession.user?.username ?? "";
    const label = session.data.mediaSession.extra?.label ?? username;
    let text = label;
    if (isPedagogy) {
      text = `${label} (Pédagogie)`;
    } else if (meetingSession?.highlighting === MeetingSessionHighlightingState.ONGOING) {
      text = `${label} (Au tableau)`;
    }
    const renderUsername = () => (
      <Text
        style={{
          fontWeight: 800,
          fontSize: focusMode ? "1.2em" : "0.8em",
          backgroundColor: isPedagogy ? "#f9af5c" : "white",
          borderRadius: "0.25em",
          padding: "0.25em 0.5em",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
        value={text}
      />
    );

    const renderFullScreenButton = () =>
      focusedVideoSessionId ? (
        <ReactIcons.CloseIconBlackBg onClick={() => setFocusedVideoSessionId(undefined)} style={{ width: "2em", height: "2em", cursor: "pointer" }} />
      ) : (
        <HoverableButton.FullScreen onClick={() => setFocusedVideoSessionId(session.getId())} style={{ width: "2em", height: "2em" }} />
      );

    if (focusMode) {
      return (
        <div className={styles.playerHeaderContainer} style={{ backgroundColor: "white", padding: "0.3em" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "1em",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                color: "white",
                fontWeight: 800,
                backgroundColor: "#05C496",
                padding: "0.5em",
                borderRadius: "0.25em",
              }}
              value={"Mode focus".toUpperCase()}
            />
            {renderUsername()}
          </div>
          {renderFullScreenButton()}
        </div>
      );
    }

    return (
      <div className={styles.playerHeaderContainer}>
        {renderUsername()}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "1em",
          }}
        >
          {renderFullScreenButton()}
        </div>
      </div>
    );
  };



  const renderPlayer = (
    session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>,
    style?: React.CSSProperties,
    focusMode = false,
    playerProps?: MeetingSessionMediaPlayerProps,
  ) => (
    <PlayerContainer.Default
      containerStyle={{
        backgroundColor: "#9E9D9B",
        overflow: "hidden",
        borderRadius: "0.5em",
        ...(style ?? {}),
      }}
      key={`ovenmedia-session-${session.getId()}-player-container`}
      overlay={focusMode ? "none" : "hover"}
      headerContent={renderPlayerHeader(session, focusMode)}
    >
      <MeetingSessionMediaPlayer
        style={{ width: "100%", height: "100%", maxHeight: "100%" }}
        {...(playerProps ?? {})}
        meeting={meeting}
        sessionId={session.data.session.id}
        mediaSessionId={session.data.mediaSession?.id!}
        ovenMediaSession={session}
        muted={true}
        zoomPanPinch={focusMode}
        callbacks={{
          playingSound: (v) => {
            playerProps?.callbacks?.playingSound?.(v);
          }
        }}
        
      />
    </PlayerContainer.Default>
  );

  const renderFocusContent = () => {
    if (!focusedVideoSessionId) return null;
    const focusedVideoSession = enabledVideoSessions.find((s) => s.getId() === focusedVideoSessionId);
    if (!focusedVideoSession) return null;
    return (
      <div
        style={{
          position: "fixed",
          display: "flex",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 99999,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            padding: "10px",
            borderRadius: "1em",
            overflow: "hidden",
          }}
        >
          {renderPlayer(
            focusedVideoSession,
            {
              minHeight: 0,
              height: "100%",
              borderTopRightRadius: "0.5em",
              borderTopLeftRadius: "0.5em",
            },
            true
          )}
        </div>
      </div>
    );
  };

  const renderNewLayoutContent = () => (
    <MeetingPlayerTriLayoutContainer
      meeting={meeting}
      items={playerItems}
      layoutProps={{ square: { noPages: true } }}
      renderItem={(rendererProvider, item) => {
        const itemData = item.getData();
        if (itemData.type !== "video_session") return rendererProvider(); // should not happen
        return renderPlayer(itemData.videoSession);
      }}
      layout={layoutIndex}
      autoOnOffVideo={false}
    />
  );

  if (studentCtx?.activityData?.mode === "pedagogy") {
    return (
      <PedagoPopup.Message
        title={"Intervention en cours..."}
        severity={"info"}
        icon={<ReactIcons.Clock style={{ width: "100%", height: "100%" }} />}
        mainMessage="Le cours va reprendre après l'intervention de la pédagogie."
        secondaryMessage="Merci de patienter."
        containerStyle={{
          background: "#00000000",
          zIndex: 0,
        }}
      />
    )
  }

  return (
    <div className={styles.container}>
      {renderNewLayoutContent()}
      {renderFocusContent()}
    </div>
  );
};
